import React from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";

export interface OnboardingStep {
  key: string;
  done: boolean;
  label: string;
  hint: string;
  action: string;
  to: string;
}

interface OnboardingStepListProps {
  steps: OnboardingStep[];
  /** Render every step's action as a text link — when another card on the screen holds the one button. */
  quiet?: boolean;
}

/** A text link styled as the second action (experience-standard §1.6). */
const QUIET_ACTION_CLASS =
  "inline-flex items-center gap-1 text-sm font-medium text-primary-40 dark:text-primary-90 hover:underline";

const actionClass = (isNext: boolean, quiet: boolean): string => {
  if (quiet) return QUIET_ACTION_CLASS;
  return isNext ? "btn btn-primary" : "btn btn-secondary";
};

/** The numbered step rows of a setup checklist; the first open step is highlighted. */
const OnboardingStepList: React.FC<OnboardingStepListProps> = ({ steps, quiet = false }) => {
  const nextKey = steps.find((s) => !s.done)?.key;

  return (
    <div className="mt-4 space-y-2">
      {steps.map((step, index) => (
        <div
          key={step.key}
          className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
            step.key === nextKey
              ? "border-primary-40 dark:border-primary-90"
              : "border-grey-90 dark:border-primary-20"
          }`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                step.done
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  : "bg-primary-90 text-primary-30 dark:bg-primary-30 dark:text-primary-90"
              }`}
            >
              {step.done ? <Check size={14} /> : index + 1}
            </span>
            <div className="min-w-0">
              <p className={`text-sm font-medium ${step.done ? "text-grey-40 dark:text-grey-60" : "text-grey-10 dark:text-grey-90"}`}>
                {step.label}
              </p>
              {!step.done && <p className="text-xs text-grey-30 dark:text-grey-70">{step.hint}</p>}
            </div>
          </div>
          {!step.done && (
            <Link to={step.to} className={actionClass(step.key === nextKey, quiet)}>
              <span>{step.action}</span>
              <ChevronRight size={16} />
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

export default OnboardingStepList;
